import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Loader2, Trophy, Fuel, AlertTriangle, Gauge, TrendingUp, Award, Truck } from "lucide-react";

/** Tab "Salud de flota" — KPIs globales del mes + ranking de vehículos por km. */
export function SaludFlotaTab() {
  const [mes, setMes] = useState(() => new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/flota/salud?mes=${mes}`).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [mes]);

  if (loading) return <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin text-amber-500 inline" /></div>;
  if (!data) return null;
  const k = data.kpis || {};

  return (
    <div className="space-y-4" data-testid="salud-flota-tab">
      <div className="flex items-center gap-2">
        <label className="text-xs text-zinc-400">Mes:</label>
        <input type="month" value={mes} onChange={(e) => setMes(e.target.value)}
          className="bg-zinc-900 border border-zinc-800 rounded px-2 py-1 text-sm" data-testid="salud-mes" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi icon={Gauge} label="Km totales" value={`${k.total_km || 0}`} unit="km" color="amber" />
        <Kpi icon={Fuel} label="Litros" value={`${k.total_litros || 0}`} unit="L" color="cyan" />
        <Kpi icon={Fuel} label="€ combustible" value={`${k.importe_combustible || 0}`} unit="€" color="emerald" />
        <Kpi icon={TrendingUp} label="Coste/km" value={k.coste_eur_por_km != null ? k.coste_eur_por_km.toFixed(3) : "—"} unit="€/km" color="zinc" />
        <Kpi icon={Gauge} label="Consumo medio" value={k.consumo_medio_l100 != null ? k.consumo_medio_l100 : "—"} unit="L/100km" color="zinc" />
        <Kpi icon={Truck} label="Partes cerrados" value={`${k.partes || 0}`} color="zinc" />
        <Kpi icon={AlertTriangle} label="Incidencias" value={`${k.incidencias || 0}`} color={k.incidencias > 0 ? "rose" : "emerald"} />
        <Kpi icon={TrendingUp} label="Vehículos activos" value={(data.ranking_vehiculos || []).length} color="zinc" />
      </div>

      {/* Ranking vehículos por km */}
      <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-4">
        <h3 className="font-bold text-sm mb-3 flex items-center gap-2">
          <Truck className="w-4 h-4 text-amber-400" /> Vehículos por kilómetros del mes
        </h3>
        {(data.ranking_vehiculos || []).length === 0 ? (
          <div className="text-xs text-zinc-500 italic text-center py-4">Sin actividad este mes</div>
        ) : (
          <div className="space-y-2">
            {data.ranking_vehiculos.map((v, idx) => {
              const max = data.ranking_vehiculos[0].km || 1;
              const pct = max > 0 ? (v.km / max) * 100 : 0;
              return (
                <div key={v.vehiculo_id} className="bg-zinc-950 border border-zinc-800 rounded p-2"
                  data-testid={`ranking-veh-${idx}`}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="font-semibold"><span className="text-amber-400 font-bold">#{idx + 1}</span> {v.vehiculo} <span className="text-zinc-500">{v.matricula}</span></span>
                    <span className="text-zinc-300 font-bold">{v.km} km</span>
                  </div>
                  <div className="w-full bg-zinc-900 rounded h-1.5 overflow-hidden">
                    <div className="h-full bg-amber-500" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="flex gap-3 text-[10px] text-zinc-500 mt-1">
                    <span>⛽ {v.litros}L · {v.importe}€</span>
                    <span>📋 {v.partes} partes</span>
                    {v.incidencias > 0 && <span className="text-rose-400">⚠ {v.incidencias} incidencias</span>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}


/** Tab "Ranking 🏆" — conductores gamificados con puntos, nivel e insignias del mes. */
export function RankingConductoresTab() {
  const [mes, setMes] = useState(() => new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/flota/ranking?mes=${mes}`).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [mes]);

  const podioTop3 = useMemo(() => (data?.ranking || []).slice(0, 3), [data]);
  const restoRanking = useMemo(() => (data?.ranking || []).slice(3), [data]);

  if (loading) return <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin text-amber-500 inline" /></div>;
  if (!data) return null;

  return (
    <div className="space-y-4" data-testid="ranking-conductores-tab">
      <div className="flex items-center gap-2">
        <label className="text-xs text-zinc-400">Mes:</label>
        <input type="month" value={mes} onChange={(e) => setMes(e.target.value)}
          className="bg-zinc-900 border border-zinc-800 rounded px-2 py-1 text-sm" data-testid="ranking-mes" />
        <span className="text-xs text-zinc-500">· {data.total_conductores} conductores</span>
      </div>

      {/* Insignias del mes */}
      {(data.insignias || []).length > 0 && (
        <div className="bg-gradient-to-br from-amber-500/10 to-yellow-500/5 border border-amber-500/30 rounded-xl p-4">
          <h3 className="font-bold text-sm mb-3 flex items-center gap-2">
            <Award className="w-4 h-4 text-amber-400" /> Insignias del mes
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {data.insignias.map((ins) => (
              <div key={ins.key} className="bg-zinc-950/60 border border-zinc-800 rounded-lg p-2 flex items-center gap-3"
                data-testid={`insignia-${ins.key}`}>
                <div className="text-3xl">{ins.emoji}</div>
                <div className="flex-1">
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">{ins.titulo}</div>
                  <div className="font-bold text-amber-300">{ins.ganador}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* PODIO TOP 3 */}
      {podioTop3.length > 0 && (
        <div className="grid grid-cols-3 gap-2" data-testid="podio">
          {/* Layout: 2º · 1º · 3º (estilo olímpico) */}
          {[1, 0, 2].map((rank) => {
            const r = podioTop3[rank];
            if (!r) return <div key={rank} />;
            const altura = rank === 0 ? "pt-3 pb-4" : rank === 1 ? "pt-6 pb-3" : "pt-7 pb-2";
            const medalla = rank === 0 ? "🥇" : rank === 1 ? "🥈" : "🥉";
            const bg = rank === 0 ? "from-amber-500/30 to-amber-500/5 border-amber-500/50"
                     : rank === 1 ? "from-zinc-400/20 to-zinc-400/5 border-zinc-400/40"
                     : "from-orange-600/20 to-orange-600/5 border-orange-600/40";
            return (
              <div key={rank} className={`bg-gradient-to-b ${bg} border-2 rounded-xl ${altura} px-2 text-center`}>
                <div className="text-3xl mb-1">{medalla}</div>
                <div className="text-[9px] uppercase tracking-wider text-zinc-400">#{rank + 1}</div>
                <div className="font-bold text-sm truncate" title={r.nombre}>{r.nombre}</div>
                <div className="text-xl font-extrabold text-amber-300">{r.puntos.toLocaleString()} pts</div>
                <div className="text-[10px] text-zinc-400 mt-1">{r.nivel_emoji} {r.nivel_nombre}</div>
                <div className="text-[10px] text-zinc-500">{r.km} km · {r.incidencias} inc.</div>
              </div>
            );
          })}
        </div>
      )}

      {/* TABLA RESTO */}
      {restoRanking.length > 0 && (
        <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-zinc-950 text-zinc-500 uppercase tracking-wider text-[10px]">
              <tr>
                <th className="px-3 py-2 text-left">#</th>
                <th className="px-3 py-2 text-left">Conductor</th>
                <th className="px-3 py-2 text-right">Pts</th>
                <th className="px-3 py-2 text-right">Nivel</th>
                <th className="px-3 py-2 text-right">Km</th>
                <th className="px-3 py-2 text-right">Partes</th>
                <th className="px-3 py-2 text-right">Inc.</th>
                <th className="px-3 py-2 text-right">Repostajes ✓</th>
              </tr>
            </thead>
            <tbody>
              {restoRanking.map((r) => (
                <tr key={r.key} className="border-t border-zinc-800 hover:bg-zinc-900/60" data-testid={`ranking-row-${r.posicion}`}>
                  <td className="px-3 py-2 font-bold text-amber-400">{r.posicion}</td>
                  <td className="px-3 py-2">
                    <div className="font-semibold">{r.tipo === "interno" ? "👤" : "🤝"} {r.nombre}</div>
                  </td>
                  <td className="px-3 py-2 text-right font-bold">{r.puntos.toLocaleString()}</td>
                  <td className="px-3 py-2 text-right">{r.nivel_emoji} <span className="text-zinc-400">{r.nivel_nombre}</span></td>
                  <td className="px-3 py-2 text-right text-zinc-300">{r.km}</td>
                  <td className="px-3 py-2 text-right text-zinc-300">{r.partes}</td>
                  <td className={`px-3 py-2 text-right ${r.incidencias > 0 ? "text-rose-300" : "text-emerald-300"}`}>{r.incidencias}</td>
                  <td className="px-3 py-2 text-right text-cyan-300">{r.repostajes_completos}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(data.ranking || []).length === 0 && (
        <div className="text-center text-zinc-500 py-8 italic">
          Sin conductores activos este mes. Cuando los montadores cierren partes empezarán a sumar puntos.
        </div>
      )}

      {/* Reglas */}
      <details className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-3">
        <summary className="text-xs text-zinc-400 cursor-pointer hover:text-amber-300">
          <Trophy className="w-3 h-3 inline mr-1" /> Cómo se calculan los puntos
        </summary>
        <div className="mt-2 space-y-1 text-[11px] text-zinc-400">
          <div>🟢 <b>+{data.puntos_config.por_km_sin_incidencia} pt</b> por cada km recorrido sin incidencia</div>
          <div>🟢 <b>+{data.puntos_config.bonus_parte_sin_incidencia} pts</b> bonus al cerrar un parte sin incidencia</div>
          <div>🟢 <b>+{data.puntos_config.bonus_repostaje_completo} pts</b> por reportar repostaje completo (litros + importe + estación)</div>
          <div>🔴 <b>{data.puntos_config.penalty_incidencia} pts</b> por cada incidencia</div>
          <div>🔴 <b>{data.puntos_config.penalty_datos_incompletos} pts</b> por parte con datos incompletos</div>
          <div>🔴 <b>{data.puntos_config.penalty_infraccion} pts</b> por cada infracción detectada</div>
          <div className="pt-2 text-[10px] text-zinc-500">Niveles: 🥉 Bronce 0-499 · 🥈 Plata 500-1999 · 🥇 Oro 2000-4999 · 💎 Diamante 5000+</div>
        </div>
      </details>
    </div>
  );
}


function Kpi({ icon: Ico, label, value, unit, color }) {
  const colors = {
    amber: "text-amber-300 border-amber-500/30 bg-amber-500/5",
    cyan: "text-cyan-300 border-cyan-500/30 bg-cyan-500/5",
    emerald: "text-emerald-300 border-emerald-500/30 bg-emerald-500/5",
    rose: "text-rose-300 border-rose-500/30 bg-rose-500/5",
    zinc: "text-zinc-200 border-zinc-700 bg-zinc-900/60",
  };
  return (
    <div className={`border rounded-lg p-3 ${colors[color] || colors.zinc}`}>
      <div className="text-[9px] uppercase tracking-wider opacity-70 flex items-center gap-1">
        <Ico className="w-2.5 h-2.5" /> {label}
      </div>
      <div className="text-xl font-extrabold mt-0.5">{value} <span className="text-xs font-normal opacity-60">{unit}</span></div>
    </div>
  );
}
